import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

/**
 * Hook to track navigation loading state
 * Shows loading indicator immediately when links are clicked and keeps it visible
 * until the page actually loads (detected by content changes)
 */
export function useNavigationLoading() {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  const previousLocationRef = useRef(location.pathname);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  // Function to hide loading - only call when we're certain page is loaded
  const hideLoading = useCallback(() => {
    cleanup();
    setIsNavigating(false);
  }, [cleanup]);

  // Function to check if page has actually loaded
  const checkIfPageLoaded = useCallback((): boolean => {
    const mainContent = document.getElementById("main-content");
    if (!mainContent) return false;

    // Check if skeleton loader is present
    const hasSkeleton = mainContent.querySelector('[class*="animate-pulse"]');

    // Check if we have real content (not just skeleton)
    const textContent = mainContent.textContent?.trim() || "";
    const hasRealContent = textContent.length > 200; // Enough text to indicate real content

    // Check if we have multiple child elements (typical of a loaded page)
    const hasMultipleElements = mainContent.children.length > 2;

    // Page is loaded if: no skeleton AND (has real content OR has multiple elements)
    return !hasSkeleton && (hasRealContent || hasMultipleElements);
  }, []);

  // Start monitoring for page load
  const startMonitoring = useCallback(() => {
    cleanup(); // Clear any existing monitoring

    let consecutiveLoadedChecks = 0;
    const requiredChecks = 3; // Need 3 consecutive checks to be sure

    checkIntervalRef.current = setInterval(() => {
      if (checkIfPageLoaded()) {
        consecutiveLoadedChecks++;
        if (consecutiveLoadedChecks >= requiredChecks) {
          // Page is definitely loaded
          hideLoading();
        }
      } else {
        // Reset counter if page not loaded yet
        consecutiveLoadedChecks = 0;
      }
    }, 100); // Check every 100ms

    // Fallback: hide after 15 seconds max (safety net)
    loadingTimeoutRef.current = setTimeout(() => {
      hideLoading();
    }, 15000);
  }, [cleanup, checkIfPageLoaded, hideLoading]);

  // Handle route changes
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;

    // Only act if path actually changed
    if (currentPath !== previousPath) {
      previousLocationRef.current = currentPath;

      // Set loading state
      setIsNavigating(true);

      // Wait a moment for React Router to start rendering, then start monitoring
      setTimeout(() => {
        startMonitoring();
      }, 100);
    }

    return cleanup;
  }, [location.pathname, startMonitoring, cleanup]);

  // Handle link clicks for immediate feedback
  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest("a[href]");

      if (link) {
        const href = link.getAttribute("href");
        const currentPath = location.pathname;

        // Only handle internal navigation links
        // Exclude anchor links (hash links) - they don't trigger page navigation
        if (
          href &&
          !href.startsWith("#") && // Exclude anchor links
          (href.startsWith("/") ||
            (!href.startsWith("http") &&
              !href.startsWith("mailto:") &&
              !href.startsWith("tel:"))) &&
          href !== currentPath
        ) {
          // Show loading immediately
          setIsNavigating(true);
        }
      }
    };

    document.addEventListener("click", handleLinkClick, true);
    return () => {
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return isNavigating;
}
